import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogTitle, DialogContent, TextField, Button, DialogActions, Typography, Box, IconButton, Select, MenuItem, InputAdornment, InputLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, CircularProgress } from '@mui/material';
import { formatApiMoneyForInput, parseColombianMoneyInput } from '../../../utils/money';

const API_URL = process.env.REACT_APP_API_URL;

export default function DialogModifyTarifa({ openDialog, handleCloseDialog, data, handleUpdateData }) {
  const [nombre, setNombre] = useState("");
  const [tarifaInicial, setTarifaInicial] = useState("");
  const [horasIniciales, setHorasIniciales] = useState("");
  const [tarifaAdicional, setTarifaAdicional] = useState("");
  const [descuento, setDescuento] = useState("");

  const [loading, setLoading] = useState(false);
  const submitRef = React.useRef(false);

  const [error, setError] = useState("");

  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (Number(descuento) >= 100) setDescuento(100)
  }, [descuento])

  // Estados de errores por campo
  const [fieldErrors, setFieldErrors] = useState({
    nombre: false,
    tarifaInicial: false,
    horasIniciales: false,
    tarifaAdicional: false,
    descuento: false,
  });

  const validateFields = () => {
    const errors = {
      nombre: nombre.trim() === "",
      tarifaInicial: tarifaInicial === "",
      horasIniciales: horasIniciales === "",
      tarifaAdicional: tarifaAdicional === "",
      descuento: descuento === "",
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  useEffect(() => {
    if (data) {
      setNombre(data.nombre || "");
      setTarifaInicial(data.tarifa_inicial == null ? "" : formatApiMoneyForInput(data.tarifa_inicial));
      setHorasIniciales(data.horas_iniciales || "");
      setTarifaAdicional(data.tarifa_adicional == null ? "" : formatApiMoneyForInput(data.tarifa_adicional));
      setDescuento(data.descuento || "");
    }
  }, [data]);

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    if (!validateFields()) {
      setError("Por favor, complete todos los campos.");
      return;
    }
    submitRef.current = true;
    setLoading(true);

    const updatedData = {
      id: data.id,
      nombre: nombre,  // Agregado aquí
      tarifa_inicial: parseColombianMoneyInput(tarifaInicial),
      horas_iniciales: parseInt(horasIniciales),
      tarifa_adicional: parseColombianMoneyInput(tarifaAdicional),
      descuento: parseFloat(descuento)
    };    

    try {
      const response = await axios.put(`${API_URL}/tarifas/${data.id}/`, updatedData).catch(err => console.log(err))
      if (response.status === 200) {
        setLoading(false);
        handleUpdateData(response.data);
        handleCloseDialog();
      } else {
        setLoading(false);
        throw new Error("Error al actualizar los datos");
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog}>

        <Backdrop
          open={loading}
          style={{ zIndex: 9999, color: '#fff' }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

      <DialogTitle>Modificar Tarifa</DialogTitle>
      <DialogContent>
          <TextField
            fullWidth
            disabled
            label="Fecha Modificación"
            value={currentTime.toLocaleDateString() + ' ' + currentTime.toLocaleTimeString()}
            variant='filled'
            color="primary" // Aplicando color azul
          />
          <TextField
            autoFocus
            margin="dense"
            label="Nombre"
            fullWidth
            value={nombre}
            sx={{ textTransform: 'uppercase' }}
            error={fieldErrors.nombre}
            helperText={fieldErrors.nombre ? "Este campo es obligatorio" : ""}
            onChange={(e) => setNombre(e.target.value.toUpperCase())}
          />

          <TextField
            autoFocus
            margin="dense"
            label="Tarifa Inicial" // 🔄 Cambio de label
            fullWidth
            type="text"
            inputMode="decimal"
            value={tarifaInicial}
            sx={{ textTransform: "uppercase" }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, maxLength: 10 }} 
            error={fieldErrors.tarifaInicial}
            helperText={fieldErrors.tarifaInicial ? "Este campo es obligatorio" : ""}
            onChange={(e) => setTarifaInicial(e.target.value.toUpperCase())}
          />

            <>
              <TextField
                autoFocus
                margin="dense"
                label="Horas Iniciales"
                fullWidth
                type="number"
                value={horasIniciales}
                sx={{ textTransform: "uppercase" }}
                error={fieldErrors.horasIniciales}
                helperText={fieldErrors.horasIniciales ? "Este campo es obligatorio" : ""}
                onChange={(e) => setHorasIniciales(e.target.value.toUpperCase())}
              />

              <TextField
                autoFocus
                margin="dense"
                label="Tarifa Adicional"
                fullWidth
                type="text"
                inputMode="decimal"
                value={tarifaAdicional}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, maxLength: 10 }} 
                sx={{ textTransform: "uppercase" }}
                error={fieldErrors.tarifaAdicional}
                helperText={fieldErrors.tarifaAdicional ? "Este campo es obligatorio" : ""}
                onChange={(e) => setTarifaAdicional(e.target.value.toUpperCase())}
              />

              <TextField
                autoFocus
                margin="dense"
                label="Descuento"
                type="number"
                fullWidth
                value={descuento}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ maxLength: 10 }}
                sx={{ textTransform: "uppercase" }}
                error={fieldErrors.descuento}
                helperText={fieldErrors.descuento ? "Este campo es obligatorio" : ""}
                onChange={(e) => setDescuento(e.target.value.toUpperCase())}
              />
            </>
      </DialogContent>
      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#d32f2f', padding: 1, borderRadius: 1, width: '95%' }}>
            <Typography sx={{ color: 'white', fontWeight: 'bold', marginRight: 1 }}>{error}</Typography>
            <IconButton color="inherit" onClick={() => setError("")}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleCloseDialog} variant="outlined" color='error'>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
